import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import FilterBar from 'views/MockServices/FilterBar/FilterBar';
import { Filter } from '../../../../store/mockServices/mockServiceTypes';
import i18n from '../../../../i18n';
import Button from 'react-bootstrap/Button';
import { useIsMount } from '../../../../hooks/useIsMountHook';
import acApi from '../../../../axios/ac/acApi';
import TransactionsToRestoreTable from './TransactionsToRestoreTable';
import _ from 'lodash';
import { applySearchTermFilterToListWithTags } from '../../../../utils/filterUtils';
import { useDispatch } from 'react-redux';
import {
  getTransaction,
  getTransactions,
  recoverTransactionsThunk,
} from '../../../../store/transactions/transactionsActions';
import SimpleLoader from '../../../../components/loaders/SimpleLoader';
import MixpanelService from '../../../../utils/mixpanelService';
import { TXN_RECOVERY_MODAL_OPEN, TXN_RECOVERY_TRIGGERED } from '../../../../utils/mixpanelEvents';

interface RestoreTransactionsModalProps {
  onHide: () => void;
}

const RestoreTransactionsModal = ({ onHide }: RestoreTransactionsModalProps) => {
  const dispatch = useDispatch();
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [filteredTxs, setFilteredTxs] = useState([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [filter, setFilter] = useState<Filter>({
    shipId: null,
    search: null,
    serviceId: null,
    status: null,
    location: null,
  });
  const isMount = useIsMount();

  useEffect(() => {
    if (isMount) {
      MixpanelService.track(TXN_RECOVERY_MODAL_OPEN());
      getArchivedTransactions(filter);
    }
  }, []);

  useEffect(() => {
    if (!isMount) {
      defineFilteredTransactions();
    }
  }, [transactions]);

  function defineFilteredTransactions() {
    let filteredTxs = _.cloneDeep(transactions);

    if (filter.serviceId && filter.serviceId > 0) {
      filteredTxs = _.filter(filteredTxs, txn => txn.serviceId == filter.serviceId);
    }

    if (!_.isEmpty(filter.search)) {
      filteredTxs = applySearchTermFilterToListWithTags(filteredTxs, filter.search);
    }

    setFilteredTxs(filteredTxs);
  }

  useEffect(() => {
    if (!isMount) {
      defineFilteredTransactions();
    }
  }, [filter]);

  const getArchivedTransactions = async (filter?: Filter) => {
    try {
      setIsLoading(true);
      const txns = await acApi.transactions.getArchivedTransactions();
      if (txns !== undefined) {
        setTransactions(txns.data.result);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const restoreHandler = async () => {
    setIsRecovering(true);
    await dispatch(recoverTransactionsThunk(selected));
    setIsRecovering(false);
    await dispatch(getTransactions());
    MixpanelService.track(TXN_RECOVERY_TRIGGERED());
    onHide();
  };

  return (
    <Modal dialogClassName="modal-full-screen" show={true} onHide={onHide} backdropClassName={'backdrop-faded'}>
      <Modal.Header closeButton>
        <h5 className="modal-title">{i18n.t('transactions:table.restoreTransactions')}</h5>
      </Modal.Header>
      <Modal.Body>
        <p>{i18n.t('transactions:table.restoreDescription')}</p>
        <FilterBar
          isGlobalServiceFilter={false}
          filter={filter}
          onChange={(flt, source) => {
            setFilter(flt);
            setSelected([]);
          }}
          disableAddService={true}
          config={{ locationFilter: false, searchFilter: true, statusFilter: false, serviceFilter: true }}
        />

        <TransactionsToRestoreTable
          selected={selected}
          transactions={filteredTxs}
          isLoading={isLoading}
          setCurrentSelected={setSelected}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={onHide}>
          {i18n.t('common:actions.cancel')}
        </Button>

        <Button variant="primary" onClick={restoreHandler} disabled={selected.length < 1 || isRecovering}>
          {isRecovering && (
            <SimpleLoader style={{ display: 'inline-block' }} showLoadingText={false} className={'mr-2'} />
          )}
          {i18n.t('common:actions.restore')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RestoreTransactionsModal;
